import { useMemo, useState } from 'react'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { MonitorSmartphone, UserPlus, Users } from 'lucide-react-native'
import { AdminScreen } from '../AdminScreen'
import { AdminTabs } from '../shared/AdminTabs'
import { DropdownMenu } from '../shared/DropdownMenu'
import { CashiersSection } from './cashiers/CashiersSection'
import { DevicesSection } from './devices/DevicesSection'
import { AddCashierModal } from './cashiers/addCashier/AddCashierModal'
import { EditCashierModal } from './cashiers/editCashier/EditCashierModal'
import { AddDeviceModal } from './devices/addDevice/AddDeviceModal'
import { EditDeviceModal } from './devices/editDevice/EditDeviceModal'
import type { Cashier } from './cashiers/useCashiers'
import { useDevices } from './devices/useDevices'

type OperationsTab = 'cashiers' | 'devices'

export function OperationsScreen() {
  const { t } = useTranslation()
  const tabs = [
    { key: 'cashiers' as const, label: t('operations.cashiers.title'), Icon: Users },
    { key: 'devices' as const, label: t('operations.devices.title'), Icon: MonitorSmartphone },
  ]
  const [activeTab, setActiveTab] = useState<OperationsTab>('cashiers')
  const [isAddCashierModalOpen, setIsAddCashierModalOpen] = useState(false)
  const [editingCashier, setEditingCashier] = useState<Cashier | null>(null)
  const [isAddDeviceModalOpen, setIsAddDeviceModalOpen] = useState(false)
  const [editingDeviceId, setEditingDeviceId] = useState<string | null>(null)
  const devicesQuery = useDevices({
    enabled: activeTab === 'devices' || isAddDeviceModalOpen,
    pollPendingDevices: isAddDeviceModalOpen,
    pollPendingDeviceId: editingDeviceId,
  })
  const devices = useMemo(() => devicesQuery.data ?? [], [devicesQuery.data])
  const editingDevice = editingDeviceId ? devices.find((device) => device.id === editingDeviceId) ?? null : null

  return (
    <AdminScreen
      title={t('adminDashboard.operationsTitle')}
      subtitle={t('adminDashboard.operationsSubtitle')}
      actions={
        <DropdownMenu
          label={t('operations.actions.new')}
          items={[
            {
              label: t('operations.actions.addCashier'),
              Icon: UserPlus,
              onPress: () => setIsAddCashierModalOpen(true),
            },
            {
              label: t('operations.actions.registerDevice'),
              Icon: MonitorSmartphone,
              onPress: () => setIsAddDeviceModalOpen(true),
            },
          ]}
          className="min-h-[44px] flex-row items-center justify-center gap-2 rounded-lg bg-accent px-5"
        />
      }
    >
      <AdminTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      <View className="mt-6">
        {activeTab === 'cashiers' ? (
          <CashiersSection
            onAddCashier={() => setIsAddCashierModalOpen(true)}
            onEditCashier={setEditingCashier}
          />
        ) : (
          <DevicesSection
            devices={devices}
            isLoading={devicesQuery.isLoading}
            isError={devicesQuery.isError}
            onRegisterDevice={() => setIsAddDeviceModalOpen(true)}
            onEditDevice={(device) => setEditingDeviceId(device.id)}
            onRetry={() => void devicesQuery.refetch()}
          />
        )}
      </View>

      <AddCashierModal
        isVisible={isAddCashierModalOpen}
        onClose={() => setIsAddCashierModalOpen(false)}
      />
      <EditCashierModal
        cashier={editingCashier}
        onClose={() => setEditingCashier(null)}
      />
      <AddDeviceModal
        isVisible={isAddDeviceModalOpen}
        onClose={() => setIsAddDeviceModalOpen(false)}
      />
      <EditDeviceModal
        device={editingDevice}
        onClose={() => setEditingDeviceId(null)}
      />
    </AdminScreen>
  )
}
